/**
 * SettledEnrolDialog — opens after a deal transitions INTO 'settled'.
 *
 * Shows:
 *   1. A read-only summary of the deal (lender, term, NAF, settlement date,
 *      origination fee, commission, balloon if > 0).
 *   2. A checkbox list of the 5 nurture automations, each with a live
 *      preview verdict from POST /automations/preview-enrol:
 *        - "Will send on 05 May 2027 (in 9 months)" → green, checkable, ticked
 *        - "Skipped — no date of birth on this contact" → amber, disabled
 *        - "Skipped — term end has already passed" → amber, disabled
 *   3. Two buttons:
 *        - "Enrol in selected" — enrols whatever is ticked
 *        - "Enrol in all applicable" — ticks then enrols only the schedulable ones
 *
 * Per Andy 2026-08-06 (Option A): this dialog is the ONLY path to enrol
 * on-settled. There is NO silent backend hook.
 */
import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, XCircle, AlertCircle, Loader2, Zap } from "lucide-react"
import { formatDate, timeUntil } from "@shared/utils/date"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { automationsApi, dealsApi } from "@/lib/api"

type PreviewVerdict =
  | "fire_today"
  | "schedule"
  | "past"
  | "below_go_live_floor"
  | "missing_reference_data"
  | "automation_not_found"
  | "automation_not_active"
  | "unknown_trigger"
  | "event_driven"

type Preview = {
  automationId: string
  name: string | null
  triggerKind?: string
  verdict: PreviewVerdict
  scheduledFor?: string
  reason?: string
}

export type SettledEnrolDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  dealId: string
  contactId: string
  automationIds: string[]
}

const CHECKABLE_VERDICTS: PreviewVerdict[] = [
  "fire_today",
  "schedule",
  "event_driven",
]

/**
 * Currency formatter — AUD, no cents.
 */
function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return "—"
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      maximumFractionDigits: 0,
    }).format(n)
  } catch {
    return `$${n}`
  }
}

function verdictLabel(p: Preview): { text: string; tone: "ok" | "warn" | "info" } {
  switch (p.verdict) {
    case "fire_today":
      return { text: "Will send today", tone: "ok" }
    case "schedule":
      if (p.scheduledFor) {
        return {
          text: `Will send ${timeUntil(new Date(p.scheduledFor + "T00:00:00+10:00"))} — ${formatDate(p.scheduledFor)}`,
          tone: "ok",
        }
      }
      return { text: "Scheduled", tone: "ok" }
    case "past":
      return { text: "Skipped — trigger date already passed", tone: "warn" }
    case "below_go_live_floor":
      return { text: "Skipped — before automation go-live", tone: "warn" }
    case "missing_reference_data":
      return {
        text: `Skipped — ${p.reason ?? "missing data on record"}`,
        tone: "warn",
      }
    case "automation_not_found":
      return {
        text: "Skipped — automation not found (may have been deleted)",
        tone: "warn",
      }
    case "automation_not_active":
      return {
        text: "Skipped — automation is paused or archived",
        tone: "warn",
      }
    case "unknown_trigger":
      return { text: "Skipped — unknown trigger type", tone: "warn" }
    case "event_driven":
      return { text: "Enrols now (event-driven)", tone: "info" }
  }
}

export function SettledEnrolDialog({
  open,
  onOpenChange,
  dealId,
  contactId,
  automationIds,
}: SettledEnrolDialogProps) {
  const qc = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [enrolResult, setEnrolResult] = useState<{
    successes: Array<{ name: string; scheduledFor?: string }>
    failures: Array<{ name: string; reason: string }>
  } | null>(null)

  // Load the deal so we can show the read-only summary.
  const dealQ = useQuery({
    queryKey: ["deal", dealId],
    queryFn: () => dealsApi.get(dealId),
    enabled: open,
  })

  // Live preview for each automation.
  const previewQ = useQuery({
    queryKey: ["preview-enrol", contactId, dealId, automationIds.join(",")],
    queryFn: () =>
      automationsApi.previewEnrol({
        contactId,
        dealId,
        automationIds,
      }),
    enabled: open && automationIds.length > 0,
    // Don't cache stale previews — deal data changes, contact may have DoB added.
    staleTime: 0,
    refetchOnMount: "always",
  })

  const previews: Preview[] = previewQ.data?.previews ?? []
  const checkableIds = useMemo(
    () =>
      new Set(
        previews
          .filter((p) => CHECKABLE_VERDICTS.includes(p.verdict))
          .map((p) => p.automationId)
      ),
    [previews]
  )

  // Default state: tick all applicable when previews load.
  useEffect(() => {
    if (previewQ.data && !previewQ.isFetching) {
      setSelectedIds(new Set(checkableIds))
    }
  }, [previewQ.data, previewQ.isFetching, checkableIds])

  const enrolMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const successes: Array<{ name: string; scheduledFor?: string }> = []
      const failures: Array<{ name: string; reason: string }> = []
      for (const automationId of ids) {
        const preview = previews.find((p) => p.automationId === automationId)
        const name = preview?.name ?? automationId
        try {
          const res = await automationsApi.enroll(automationId, {
            contactId,
            // Only pass dealId for deal-scoped triggers. term_end +
            // settlement_anniversary get the deal. birthday does not.
            dealId:
              preview?.triggerKind === "birthday" ? undefined : dealId,
            confirmFuture: true,
          })
          if (res.enrolled > 0) {
            successes.push({ name, scheduledFor: res.scheduledFor })
          } else {
            failures.push({
              name,
              reason: res.skipReason ?? "no_matching_automation",
            })
          }
        } catch (err) {
          failures.push({
            name,
            reason:
              err instanceof Error ? err.message : "Enrolment failed",
          })
        }
      }
      return { successes, failures }
    },
    onSuccess: (result) => {
      setEnrolResult(result)
      qc.invalidateQueries({ queryKey: ["automations"] })
      qc.invalidateQueries({ queryKey: ["deal", dealId] })
    },
  })

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const enrolSelected = () => {
    const ids = [...selectedIds].filter((id) => checkableIds.has(id))
    if (ids.length === 0) return
    enrolMutation.mutate(ids)
  }

  const enrolAllApplicable = () => {
    const ids = [...checkableIds]
    setSelectedIds(new Set(ids))
    if (ids.length === 0) return
    enrolMutation.mutate(ids)
  }

  const deal = dealQ.data?.deal
  const balloon = deal?.balloon ?? null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-600" />
            Deal settled — enrol into nurture automations
          </DialogTitle>
        </DialogHeader>

        {enrolResult ? (
          // ────── Post-enrol success screen ──────
          <div className="space-y-3 pt-2">
            {enrolResult.successes.length > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <div className="text-sm">
                    <div className="font-medium text-emerald-900">
                      Enrolled into {enrolResult.successes.length}{" "}
                      {enrolResult.successes.length === 1
                        ? "automation"
                        : "automations"}
                    </div>
                    <ul className="mt-1 space-y-0.5 text-emerald-800/90 text-xs">
                      {enrolResult.successes.map((s, i) => (
                        <li key={i}>
                          • {s.name}
                          {s.scheduledFor && (
                            <span className="text-emerald-700/70">
                              {" "}
                              — will send {formatDate(s.scheduledFor)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {enrolResult.failures.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div className="text-sm">
                    <div className="font-medium text-amber-900">
                      {enrolResult.failures.length}{" "}
                      {enrolResult.failures.length === 1
                        ? "automation was"
                        : "automations were"}{" "}
                      skipped
                    </div>
                    <ul className="mt-1 space-y-0.5 text-amber-800/90 text-xs">
                      {enrolResult.failures.map((f, i) => (
                        <li key={i}>
                          • {f.name}: {f.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          // ────── Preview + selection screen ──────
          <div className="space-y-4 pt-2">
            {/* Deal summary — read-only */}
            <div className="rounded-md border bg-muted/30 p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                Settled deal
              </div>
              {dealQ.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : deal ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <div>
                    <span className="text-muted-foreground">Lender:</span>{" "}
                    <span className="font-medium">{deal.lender ?? "—"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Settlement date:</span>{" "}
                    <span className="font-medium">
                      {deal.settledDate ? formatDate(deal.settledDate) : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Loan term:</span>{" "}
                    <span className="font-medium">
                      {deal.termMonths ? `${deal.termMonths} months` : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Term end:</span>{" "}
                    <span className="font-medium">
                      {deal.termEndDate ? formatDate(deal.termEndDate) : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">NAF:</span>{" "}
                    <span className="font-medium">{fmtCurrency(deal.naf)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Commission:</span>{" "}
                    <span className="font-medium">
                      {fmtCurrency(deal.commission)}
                    </span>
                  </div>
                  {/* Origination fee is not currently a field on the deal
                      schema (raised with Andy 2026-08-06). If added later,
                      surface it here. */}
                  {balloon != null && balloon > 0 && (
                    <div>
                      <span className="text-muted-foreground">Balloon:</span>{" "}
                      <span className="font-medium">
                        {fmtCurrency(balloon)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Deal details unavailable.
                </div>
              )}
            </div>

            {/* Automation checklist */}
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                Automations
              </div>
              {previewQ.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking which automations apply…
                </div>
              ) : previews.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4">
                  No automations configured for auto-enrol on settlement.
                </div>
              ) : (
                <ul className="space-y-2">
                  {previews.map((p) => {
                    const checkable = checkableIds.has(p.automationId)
                    const checked = selectedIds.has(p.automationId)
                    const label = verdictLabel(p)
                    return (
                      <li
                        key={p.automationId}
                        className={`flex items-start gap-3 rounded-md border p-3 ${
                          checkable
                            ? "border-input bg-background"
                            : "border-muted bg-muted/30 opacity-70"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-input"
                          checked={checked}
                          disabled={!checkable || enrolMutation.isPending}
                          onChange={() => toggle(p.automationId)}
                        />
                        <div className="flex-1 text-sm">
                          <div className="font-medium">
                            {p.name ?? p.automationId}
                          </div>
                          <div
                            className={`text-xs mt-0.5 flex items-center gap-1 ${
                              label.tone === "ok"
                                ? "text-emerald-700"
                                : label.tone === "warn"
                                  ? "text-amber-700"
                                  : "text-blue-700"
                            }`}
                          >
                            {label.tone === "ok" ? (
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            ) : label.tone === "warn" ? (
                              <XCircle className="h-3.5 w-3.5" />
                            ) : (
                              <AlertCircle className="h-3.5 w-3.5" />
                            )}
                            {label.text}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {enrolMutation.isError && (
              <div className="text-sm text-destructive">
                {enrolMutation.error instanceof Error
                  ? enrolMutation.error.message
                  : "Enrolment failed"}
              </div>
            )}

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={enrolMutation.isPending}
              >
                Skip — don't enrol
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={enrolAllApplicable}
                  disabled={
                    checkableIds.size === 0 ||
                    enrolMutation.isPending ||
                    previewQ.isLoading
                  }
                >
                  {enrolMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enrolling…
                    </>
                  ) : (
                    `Enrol in all applicable (${checkableIds.size})`
                  )}
                </Button>
                <Button
                  onClick={enrolSelected}
                  disabled={
                    selectedIds.size === 0 ||
                    enrolMutation.isPending ||
                    previewQ.isLoading
                  }
                >
                  {enrolMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enrolling…
                    </>
                  ) : (
                    `Enrol in selected (${selectedIds.size})`
                  )}
                </Button>
              </div>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
